import {
  EDOCUMENT_STATUS_LABELS,
  edocumentStatusSchema,
  INVOICE_DIRECTION_LABELS,
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
  type InvoiceDirection,
  invoiceDirectionSchema,
  type InvoiceSummary,
  invoiceStatusSchema,
  invoiceTypeSchema,
} from "@station/contracts";
import {
  Alert,
  Button,
  DataTable,
  DateInput,
  EmptyState,
  FormField,
  Input,
  Select,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@station/ui";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { FilePlus2, ReceiptText } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../features/accounting/company.js";
import { useCompanyStations } from "../../../features/accounting/shared.js";
import { CounterpartyIdCombobox } from "../../../features/counterparties/counterparty-combobox.js";
import { useInvoiceColumns } from "../../../features/invoicing/columns.js";
import { InvoicingPage, useInvoicingPermissions } from "../../../features/invoicing/shared.js";
import { api } from "../../../lib/api.js";
import { useCursorPages } from "../../../lib/cursor-pages.js";
import { errorMessage } from "../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../lib/excel.js";
import { queryKeys } from "../../../lib/query.js";
import { useTenantShape } from "../../../lib/tenant-shape.js";

const date = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);
const searchSchema = z.object({
  ...companySearchShape,
  tab: invoiceDirectionSchema.optional().catch(undefined),
  status: invoiceStatusSchema.optional().catch(undefined),
  edocumentStatus: edocumentStatusSchema.optional().catch(undefined),
  invoiceType: invoiceTypeSchema.optional().catch(undefined),
  counterpartyId: z.string().uuid().optional().catch(undefined),
  stationId: z.string().uuid().optional().catch(undefined),
  from: date,
  to: date,
  q: z.string().max(100).optional().catch(undefined),
});
type InvoiceSearch = z.infer<typeof searchSchema>;

export const Route = createFileRoute("/_app/faturalar/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: InvoiceListPage,
});

const DIRECTIONS: InvoiceDirection[] = ["SALES", "PURCHASE"];

function InvoiceListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const search = Route.useSearch();
  const permissions = useInvoicingPermissions();
  const tab = search.tab ?? "SALES";
  const canCreate = permissions.canManageSales || permissions.canManagePurchase;
  return (
    <InvoicingPage
      title={t("menu.invoices")}
      description={t("invoicing.list.description")}
      actions={
        canCreate ? (
          <Button asChild>
            <Link to="/faturalar/yeni" search={{ direction: tab }}>
              <FilePlus2 aria-hidden="true" />
              {t("menu.invoiceNew")}
            </Link>
          </Button>
        ) : null
      }
    >
      {({ companyId }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({
              // Sekme değişince süzgeçler korunur; sayfa sıfırlanır.
              search: (previous) => ({ ...previous, tab: next as InvoiceDirection }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("invoicing.list.tabs")}>
            {DIRECTIONS.map((direction) => (
              <TabsTrigger key={direction} value={direction}>
                {INVOICE_DIRECTION_LABELS[direction]}
              </TabsTrigger>
            ))}
          </TabsList>
          {DIRECTIONS.map((direction) => (
            <TabsContent key={direction} value={direction}>
              {tab === direction ? (
                <InvoiceList key={direction} companyId={companyId} direction={direction} search={search} />
              ) : null}
            </TabsContent>
          ))}
        </Tabs>
      )}
    </InvoicingPage>
  );
}

function InvoiceList({
  companyId,
  direction,
  search,
}: {
  companyId: string;
  direction: InvoiceDirection;
  search: InvoiceSearch;
}): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate({ from: Route.fullPath });
  const shape = useTenantShape();
  const stations = useCompanyStations(companyId);
  const columns = useInvoiceColumns();
  const [searchText, setSearchText] = useState(search.q ?? "");
  const setFilters = (patch: Partial<InvoiceSearch>) =>
    void navigate({
      search: (previous) =>
        Object.fromEntries(Object.entries({ ...previous, ...patch }).filter(([, value]) => value)),
      replace: true,
    });

  useEffect(() => {
    const handle = setTimeout(() => {
      if ((search.q ?? "") !== searchText.trim()) setFilters({ q: searchText.trim() || undefined });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- yalnızca yazılan metin değişince çalışmalı
  }, [searchText]);

  const query = {
    companyId,
    limit: 25,
    direction,
    ...(search.status ? { status: search.status } : {}),
    ...(search.edocumentStatus ? { edocumentStatus: search.edocumentStatus } : {}),
    ...(search.invoiceType ? { invoiceType: search.invoiceType } : {}),
    ...(search.counterpartyId ? { counterpartyId: search.counterpartyId } : {}),
    ...(search.stationId ? { stationId: search.stationId } : {}),
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
    ...(search.q ? { search: search.q } : {}),
  };
  const pages = useCursorPages(JSON.stringify(query));
  const list = useQuery({
    queryKey: queryKeys.invoicing(companyId, "invoices", { ...query, cursor: pages.cursor ?? null }),
    placeholderData: keepPreviousData,
    queryFn: async () =>
      (
        await api.GET("/invoices", {
          params: { query: { ...query, ...(pages.cursor ? { cursor: pages.cursor } : {}) } },
        })
      ).data!,
  });

  const rows = list.data?.items ?? [];
  const queued = rows.filter((row) => row.ledgerStatus === "QUEUED").length;

  return (
    <div className="grid gap-4">
      {queued > 0 ? (
        <Alert tone="warning" title={t("invoicing.list.ledgerQueuedTitle")}>
          {t("invoicing.list.ledgerQueuedBody", { count: queued })}{" "}
          <Link to="/muhasebe/eslestirmeler" className="underline underline-offset-4">
            {t("menu.accountMappings")}
          </Link>
        </Alert>
      ) : null}

      <DataTable
        caption={t("invoicing.list.caption", { direction: INVOICE_DIRECTION_LABELS[direction] })}
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<ReceiptText />}
            title={t("invoicing.list.emptyTitle")}
            description={t("invoicing.list.emptyDescription")}
          />
        }
        filters={
          <>
            <FormField label={t("common.status")} className="w-40">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) =>
                    setFilters({
                      status:
                        event.target.value === "" ? undefined : invoiceStatusSchema.parse(event.target.value),
                    })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  {invoiceStatusSchema.options.map((status) => (
                    <option key={status} value={status}>
                      {INVOICE_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("invoicing.fields.edocumentStatus")} className="w-44">
              {(control) => (
                <Select
                  {...control}
                  value={search.edocumentStatus ?? ""}
                  onChange={(event) =>
                    setFilters({
                      edocumentStatus:
                        event.target.value === ""
                          ? undefined
                          : edocumentStatusSchema.parse(event.target.value),
                    })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  {edocumentStatusSchema.options.map((status) => (
                    <option key={status} value={status}>
                      {EDOCUMENT_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("invoicing.fields.invoiceType")} className="w-36">
              {(control) => (
                <Select
                  {...control}
                  value={search.invoiceType ?? ""}
                  onChange={(event) =>
                    setFilters({
                      invoiceType:
                        event.target.value === "" ? undefined : invoiceTypeSchema.parse(event.target.value),
                    })
                  }
                >
                  <option value="">{t("common.all")}</option>
                  {invoiceTypeSchema.options.map((type) => (
                    <option key={type} value={type}>
                      {INVOICE_TYPE_LABELS[type]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("invoicing.fields.counterparty")} className="w-64">
              {(control) => (
                <CounterpartyIdCombobox
                  {...control}
                  companyId={companyId}
                  counterpartyId={search.counterpartyId ?? null}
                  onChange={(id) => setFilters({ counterpartyId: id ?? undefined })}
                />
              )}
            </FormField>
            {shape.isSingleStation ? null : (
              <FormField label={t("invoicing.fields.station")} className="w-48">
                {(control) => (
                  <Select
                    {...control}
                    value={search.stationId ?? ""}
                    onChange={(event) => setFilters({ stationId: event.target.value || undefined })}
                  >
                    <option value="">{t("common.all")}</option>
                    {(stations.data ?? []).map((station) => (
                      <option key={station.id} value={station.id}>
                        {station.name}
                      </option>
                    ))}
                  </Select>
                )}
              </FormField>
            )}
            <FormField label={t("invoicing.list.from")} className="w-40">
              {(control) => (
                <DateInput
                  {...control}
                  value={search.from ?? null}
                  onValueChange={(value) => setFilters({ from: value ?? undefined })}
                />
              )}
            </FormField>
            <FormField label={t("invoicing.list.to")} className="w-40">
              {(control) => (
                <DateInput
                  {...control}
                  value={search.to ?? null}
                  onValueChange={(value) => setFilters({ to: value ?? undefined })}
                />
              )}
            </FormField>
            <FormField label={t("common.search")} className="w-56">
              {(control) => (
                <Input
                  {...control}
                  type="search"
                  value={searchText}
                  placeholder={t("invoicing.list.searchPlaceholder")}
                  onChange={(event) => setSearchText(event.target.value)}
                />
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const all = await fetchAllPages<InvoiceSummary>(
            async (cursor) =>
              (
                await api.GET("/invoices", {
                  params: { query: { ...query, limit: 200, ...(cursor ? { cursor } : {}) } },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "faturalar",
            sheetName: INVOICE_DIRECTION_LABELS[direction],
            columns: visible,
            rows: all,
          });
        }}
      />
    </div>
  );
}
